// Phase 20 · Community V3 · Event card
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Video, MessageSquare, Users, Radio, PenTool, HelpCircle } from "lucide-react";

const TYPE_META = {
    live_stream: { label: "Live", icon: Radio, color: "text-[#FAFAFA] border-[#FAFAFA]/40 bg-[#FAFAFA]/10" },
    webinar: { label: "Webinar", icon: Video, color: "text-[#FFFFFF] border-[#FFFFFF]/40 bg-[#FFFFFF]/10" },
    workshop: { label: "Workshop", icon: PenTool, color: "text-[#CCCCCC] border-[#CCCCCC]/40 bg-[#CCCCCC]/10" },
    qa_ama: { label: "Q&A / AMA", icon: HelpCircle, color: "text-[#CCCCCC] border-[#CCCCCC]/40 bg-[#CCCCCC]/10" },
};

function useCountdown(iso) {
    const [rem, setRem] = useState(0);
    useEffect(() => {
        const tgt = new Date(iso).getTime();
        const tick = () => setRem(Math.max(0, tgt - Date.now()));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [iso]);
    if (rem === 0) return null;
    const days = Math.floor(rem / 86400000);
    const hours = Math.floor((rem % 86400000) / 3600000);
    const mins = Math.floor((rem % 3600000) / 60000);
    if (days > 0) return `in ${days}d ${hours}h`;
    if (hours > 0) return `in ${hours}h ${mins}m`;
    if (mins > 0) return `in ${mins}m`;
    return "starting soon";
}

export default function EventCard({ event, onClick }) {
    const meta = TYPE_META[event.event_type] || TYPE_META.webinar;
    const Icon = meta.icon;
    const countdown = useCountdown(event.scheduled_start);
    const isLive = event.status === "live";

    return (
        <Link
            to={`/events/${event.slug || event.id}`}
            onClick={onClick}
            data-testid={`event-card-${event.slug || event.id}`}
            className="group relative bg-[#111111] border border-[#1A1A1A] rounded-2xl overflow-hidden hover:border-[#FFFFFF]/50 transition-all block"
        >
            <div className="aspect-video bg-gradient-to-br from-[#111111] to-[#1A1A1A] relative overflow-hidden">
                {event.cover_image_url ? (
                    <img
                        src={event.cover_image_url}
                        alt={event.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Icon size={44} className={meta.color.split(" ")[0]} />
                    </div>
                )}
                <div className={`absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-bold uppercase tracking-widest ${meta.color}`}>
                    <Icon size={11} /> {meta.label}
                </div>
                {isLive && (
                    <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#FAFAFA] text-white text-[10px] font-bold uppercase tracking-widest animate-pulse">
                        <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE NOW
                    </div>
                )}
                {!isLive && countdown && (
                    <div className="absolute bottom-2 right-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-neutral-100 text-[10px] font-bold tracking-widest">
                        {countdown}
                    </div>
                )}
            </div>
            <div className="p-3 space-y-1.5">
                <h3 className="text-sm font-semibold text-neutral-100 group-hover:text-[#FFFFFF] transition-colors line-clamp-2">
                    {event.title}
                </h3>
                <div className="flex items-center gap-3 text-[11px] text-neutral-500">
                    <span className="inline-flex items-center gap-1">
                        <Calendar size={11} />
                        {new Date(event.scheduled_start).toLocaleString([], {
                            month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
                        })}
                    </span>
                    <span className="inline-flex items-center gap-1">
                        <Users size={11} /> {event.attendee_count || 0}
                    </span>
                </div>
                <div className="flex items-center gap-2 pt-1">
                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center text-neutral-900 text-[10px] font-bold">
                        {event.host_username?.[0]?.toUpperCase() || "?"}
                    </div>
                    <span className="text-[11px] text-neutral-400">@{event.host_username}</span>
                    {event.viewer_rsvp_status === "registered" && (
                        <span className="ml-auto text-[10px] px-2 py-0.5 rounded-full bg-[#CCCCCC]/10 border border-[#CCCCCC]/40 text-[#CCCCCC] font-semibold">
                            RSVP'd
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
}
